import { DEFAULT_LOCALE, LOCALES, isLocale, type Locale } from "@/i18n/locales";
import { NAV_ROUTE_IDS } from "@/lib/nav";
import { env } from "@/lib/env";

// The ONE source the router, the language switcher, the hreflang set and the
// sitemap generator all read. The sibling purewallet-website records why this
// matters: a hand-listed route table drifts from the sitemap and the hreflang
// pairs, and nothing fails when it does.

/** Every routable page id. `/search` is routable but deliberately unindexed. */
export const ROUTE_IDS = [...NAV_ROUTE_IDS, "/search"] as const;

export type RouteId = (typeof ROUTE_IDS)[number];

/** The subset that belongs in the sitemap — `/search` is a redirect surface. */
export const INDEXABLE_ROUTE_IDS: readonly RouteId[] = NAV_ROUTE_IDS;

/**
 * English keeps the bare path so no already-indexed URL moves; German is
 * additive under `/de`. Route SLUGS are never translated — one route table
 * serves both locales, which is what keeps the hreflang pairs derivable rather
 * than authored.
 */
export function pathFor(routeId: RouteId, locale: Locale): string {
  if (locale === DEFAULT_LOCALE) return routeId;
  return routeId === "/" ? `/${locale}` : `/${locale}${routeId}`;
}

export function urlFor(routeId: RouteId, locale: Locale): string {
  return `${env.publicDomain}${pathFor(routeId, locale)}`;
}

/** Every (routeId, locale) pair — the source for the sitemap and hreflang sets. */
export function allLocalisedPaths(): ReadonlyArray<{
  routeId: RouteId;
  locale: Locale;
  path: string;
}> {
  return ROUTE_IDS.flatMap((routeId) =>
    LOCALES.map((locale) => ({ routeId, locale, path: pathFor(routeId, locale) })),
  );
}

/**
 * Split a pathname into its locale and the canonical route beneath it. Returns
 * `null` for anything that is not a real route — which is what lets the router
 * send it to NotFound instead of silently rendering the homepage at a 200.
 */
export function resolvePath(pathname: string): { locale: Locale; routeId: RouteId } | null {
  const clean = pathname.replace(/\/+$/, "") || "/";
  const segments = clean.split("/").filter(Boolean);

  let locale: Locale = DEFAULT_LOCALE;
  let rest = segments;
  if (segments.length > 0 && isLocale(segments[0]!)) {
    locale = segments[0] as Locale;
    rest = segments.slice(1);
  }

  const routeId = (rest.length === 0 ? "/" : `/${rest.join("/")}`) as RouteId;
  return (ROUTE_IDS as readonly string[]).includes(routeId) ? { locale, routeId } : null;
}

/** The counterpart path for the same page in another locale. */
export function switchLocalePath(pathname: string, target: Locale): string {
  const resolved = resolvePath(pathname);
  return pathFor(resolved?.routeId ?? "/", target);
}

export function localeFromPath(pathname: string): Locale {
  return resolvePath(pathname)?.locale ?? DEFAULT_LOCALE;
}
